import React, { Suspense } from "react";
import { ChakraProvider, theme, Spinner, Center } from "@chakra-ui/react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ApolloProvider } from "@apollo/client";
import { apolloClient } from "./apollo/client";
import Home from "./pages/Home";
import OneSolarIncentivesLandingPage from "./pages/OneSolarIncentivesLandingPage";
import OneSolarThanksYou from "./pages/OneSolarThanksYou";
import EasyPropertyInvestorVariation1 from "./pages/EasyPropertyInvestorVariation1";
import Podcast from "./pages/Podcast";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsAndConditions from "./pages/TermsAndConditions";
import CookiePolicy from "./pages/CookiePolicy";
import { brandConfig } from "./brandConfig";

// Module configurations
import clientsModuleConfig from "./pages/clients/moduleConfig";
import passwordsModuleConfig from "./pages/passwords/moduleConfig";
import employeesModuleConfig from "./pages/employees/moduleConfig";
import companiesModuleConfig from "./pages/companies/moduleConfig";
import assetsModuleConfig from "./pages/assets/moduleConfig";
import { calendarsModuleConfig } from "./pages/calendars/moduleConfig";

/* 
 * MIGRATION NOTE: We are transitioning to a dynamic module routing system.
 * 
 * The Clients module has been converted as the example pattern.
 * As we update each module, we will:
 * 1. Create/update its moduleConfig.ts file (see clients/moduleConfig.ts as example)
 * 2. Import the moduleConfig here
 * 3. Add it to the modules array below
 * 4. Remove its hardcoded routes from below
 * 
 * This allows modules to be truly plug-and-play with automatic route registration
 * and centralized permission management.
 * 
 * TO CONVERT: Projects, Bills, Products, Sessions, Emails, etc.
 */

// Import other module configs here as you convert them:
// import projectsModuleConfig from "./pages/projects/moduleConfig";
// import billsModuleConfig from "./pages/bills/moduleConfig";
// import productsModuleConfig from "./pages/products/moduleConfig";

// Combine all module configurations
const modules = [
  clientsModuleConfig,
  passwordsModuleConfig,
  employeesModuleConfig,
  companiesModuleConfig,
  assetsModuleConfig,
  calendarsModuleConfig,
  // Add other modules here as you convert them:
  // projectsModuleConfig,
  // billsModuleConfig,
  // productsModuleConfig,
];

// Protected Route Component
import { ProtectedRoute } from "./components/ProtectedRoute";

// Loading component for lazy-loaded routes
const LoadingFallback = () => (
  <Center h="100vh">
    <Spinner size="xl" color={brandConfig.colors.primary} />
  </Center>
);

// Bill-related imports
import Bills from "./pages/bills/Bills";
import BillDetails from "./pages/bills/BillDetails";
import BillPDFPreview from "./pages/bills/BillPDFPreview";
import NewBill from "./pages/bills/NewBill";
import BillPaymentSuccess from "./pages/bills/BillPaymentSuccess";
import BillChaserPage from "./pages/admin/billChaserPage";
import PaymentReceivingDetails from "./pages/bills/PaymentReceivingDetails";
import EditPaymentDetails from "./pages/bills/EditPaymentDetails";
import BillingDetails from "./pages/bills/BillingDetails";



// Product-related imports
import NewProductForm from "./pages/products/NewProductForm";
import ProductView from "./pages/products/ProductView";
import AllProducts from "./pages/products/AllProducts";
import Cart from "./pages/products/Cart";
import OrderDetails from "./pages/products/OrderDetails";

// Project-related imports
import NewProject from "./pages/projects/new";
import ProjectPage from "./pages/projects/ProjectPage";
import ProjectsList from "./pages/projects/ProjectsList";
import TimelineView from "./pages/projects/TimelineView";

// Email-related imports
import EmailsList from "./pages/emails/EmailsList";
import NewEmail from "./pages/emails/new";
import EmailView from "./pages/emails/EmailView";
import InboundEmailDetail from "./pages/emails/InboundEmailDetail";
import { AddressBook } from "./pages/emails/AddressBook";
import { EmailAccountsAdmin } from "./pages/emails/EmailAccountsAdmin";

// Session/Booking-related imports
import { ClientBookingCalendar } from "./pages/sessions/ClientBookingCalendar";
import { ScheduleSession } from "./pages/sessions/ScheduleSession";
import { EditSessionType } from "./pages/sessions/EditSessionType";
import { CreateSessionType } from "./pages/sessions/CreateSessionType";

// Knowledge Base imports
import { KnowledgeBaseList, ArticleViewer } from "./pages/knowledgebase";
import { SessionTypesPage } from "./pages/sessions/SessionTypes";
import { SetAvailabilities } from "./pages/sessions/SetAvailabilities";
import { BookingDetails } from "./pages/sessions/BookingDetails";
import { ClientBookingCalendarByPractitionerId } from "./pages/sessions/ClientBookingCalendarByPractitionerId";

// Subscription/Billing-related imports
import SubscriptionsDashboard from "./pages/subscriptions/SubscriptionsDashboard";
import ManageSubscriptions from "./pages/subscriptions/ManageSubscriptions";
import PaymentMethods from "./pages/subscriptions/PaymentMethods";
import InvoiceHistory from "./pages/subscriptions/InvoiceHistory";
import BillingSettings from "./pages/subscriptions/BillingSettings";
import CreateSubscription from "./pages/subscriptions/CreateSubscription";
import ManualCharging from "./pages/subscriptions/ManualCharging";
import EditSubscription from "./pages/subscriptions/EditSubscription";

// Admin and dashboard imports
import AdminDashboard from "./pages/AdminDashboard";
import DeploymentManager from "./pages/admin/DeploymentManager";
import TenantDeploymentManager from "./pages/admin/TenantDeploymentManager";

// Phone System imports
import { PhoneSystemDashboard } from "./pages/phone-system/PhoneSystemDashboard";
import { PhoneNumbersList } from "./pages/phone-system/PhoneNumbersList";
import { CallRecordings } from "./pages/phone-system/CallRecordings";
import { BrowserCall } from "./pages/phone-system/BrowserCall";
import { PhoneNumberAssignmentAdmin } from "./pages/phone-system/PhoneNumberAssignmentAdmin";

// Vapi Voice AI imports
import { VapiDashboard } from "./pages/vapi/VapiDashboard";
import { WebCall } from "./pages/vapi/WebCall";
import { CallLogs } from "./pages/vapi/CallLogs";
import { CallDetails } from "./pages/vapi/CallDetails";
import { Workflows } from "./pages/vapi/Workflows";
import { VapiTodos } from "./pages/vapi/VapiTodos";

// Test and development imports
import SendTestEmail from "./pages/test/SendTestEmail";
import TestUploadFile from "./pages/test/TestUploadFile";
import TestUploadEncryptedFile from "./pages/test/TestUploadEncryptedFile";
import TestAuth from "./pages/test/TestAuth";
import TestClientCart from "./pages/test/TestClientCart";
import Event1Page from "./pages/events/tom/1";
import Event2Page from "./pages/events/tom/2";
import Event3Page from "./pages/events/tom/3";
import Event4Page from "./pages/events/tom/4";
import Block52EventPage from "./pages/events/tom/Block52";
import ChrisEventPage from "./pages/events/tom/chris";

// Contexts and Layout
import { AuthProvider } from "./contexts/AuthContext";
import CreateTenant from "./pages/admin/CreateTenant";
import TenantsList from "./pages/admin/TenantsList";
import EditTenant from "./pages/admin/EditTenant";

// Profile-related imports
import ViewProfile from "./pages/profile/ViewProfile";
import EditProfile from "./pages/profile/EditProfile";
import Settings from "./pages/profile/Settings";
import ShippingAddresses from "./pages/profile/ShippingAddresses";
import ProfilePaymentMethods from "./pages/profile/PaymentMethods";
import SubscriptionOffers from "./pages/profile/SubscriptionOffers";
import CurrentSubscriptions from "./pages/profile/CurrentSubscriptions";

// Seller Profile imports
import NewSellerProfile from "./pages/sellerprofile/NewSellerProfile";
import SellerProfilesList from "./pages/sellerprofile/SellerProfilesList";
import SellerProfileView from "./pages/sellerprofile/SellerProfileView";

// Research & Development imports
import ResearchAndDesignDashboard from "./pages/researchanddesign";
import ResearchAndDesignProjects from "./pages/researchanddesign/projects";
import ResearchAndDesignProjectWizard from "./pages/researchanddesign/new";
import ResearchAndDesignProjectDetail from "./pages/researchanddesign/project";
import ResearchAndDesignProjectEdit from "./pages/researchanddesign/edit";
import ResearchAndDesignTimesheet from "./pages/researchanddesign/timesheet";
import ResearchAndDesignEvidence from "./pages/researchanddesign/evidence";
import ResearchAndDesignUploadEvidence from "./pages/researchanddesign/upload-evidence";
import ResearchAndDesignEvidenceDetails from "./pages/researchanddesign/evidence-details";
import ResearchAndDesignRecordAudio from "./pages/researchanddesign/record-audio";
import ResearchAndDesignGapAnalysis from "./pages/researchanddesign/gaps";
import ResearchAndDesignReports from "./pages/researchanddesign/reports";
import ResearchAndDesignNewActivity from "./pages/researchanddesign/new-activity";
import ResearchAndDesignActivityDetails from "./pages/researchanddesign/activity-details";
import ResearchAndDesignEditActivity from "./pages/researchanddesign/edit-activity";
import ResearchAndDesignTranscribe from "./pages/researchanddesign/transcribe";
// import ShippingAddresses from "./pages/profile/ShippingAddresses";

// Training Center imports
import HamishTraining from "./pages/hamish";
// import PaymentMethods from "./pages/profile/PaymentMethods";

// Domains module imports
import { DomainsPage } from "./pages/domains/index";
import { DomainDetails } from "./pages/domains/DomainDetails";

// Offers module imports  
import { MaintenanceOffer } from "./pages/offers/maintenance";

// Floating navbar component
// import { FloatingNavbar } from "./components/FloatingNavbar";
import { useAuth } from "./contexts/AuthContext";

const AppContent = () => {
  const { isAuthenticated } = useAuth();
  
  return (
    <>
      {/* Show floating navbar only when authenticated */}
      {/* {isAuthenticated && <FloatingNavbar />} */}
      
      {/* Each page now includes its own NavbarWithCallToAction and FooterWithFourColumns for consistency */}
      <Routes>
        {/* Home page - Using One Solar Incentives Landing Page */}
        <Route path="/" element={<OneSolarIncentivesLandingPage />} />
        
        {/* Thank you page */}
        <Route path="/thankyou" element={<OneSolarThanksYou />} />
        
        {/* Apple-inspired variation */}
        <Route path="/variation1" element={<EasyPropertyInvestorVariation1 />} />
        <Route path="/variation1/*" element={<EasyPropertyInvestorVariation1 />} />
        
        {/* Podcast page */}
        <Route path="/podcast" element={<Podcast />} />
        
        {/* Legal pages */}
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
        <Route path="/cookie-policy" element={<CookiePolicy />} />

        {/* Event routes */}
        <Route path="/events/tom/1" element={<Event1Page />} />
        <Route path="/events/tom/2" element={<Event2Page />} />
        <Route path="/events/tom/3" element={<Event3Page />} />
        <Route path="/events/tom/4" element={<Event4Page />} />
        <Route path="/events/tom/block52" element={<Block52EventPage />} />
        <Route path="/events/tom/chris" element={<ChrisEventPage />} />

        {/* Dynamic Module Routes */}
        {modules.map((module) => 
          module.routes?.map((route) => (
            <Route
              key={route.path}
              path={route.path}
              element={
                route.permissions ? (
                  <ProtectedRoute requiredPermissions={route.permissions}>
                    <Suspense fallback={<LoadingFallback />}>
                      <route.component />
                    </Suspense>
                  </ProtectedRoute>
                ) : (
                  <Suspense fallback={<LoadingFallback />}>
                    <route.component />
                  </Suspense>
                )
              }
            />
          ))
        )}

        {/* Website routes */}

        {/* Simple Bills routes */}
        <Route path="/bills" element={<Bills />} />
        <Route path="/bills/new" element={<NewBill />} />
        <Route path="/bills/payment-details" element={<PaymentReceivingDetails />} />
        <Route path="/bills/payment-details/edit" element={<EditPaymentDetails />} />
        <Route path="/bills/billing-details" element={<BillingDetails />} />
        <Route path="/bill/:id" element={<BillDetails />} />
        <Route path="/bill/:id/preview" element={<BillPDFPreview />} />
        <Route path="/bill/:id/payment-success" element={<BillPaymentSuccess />} />
        <Route path="/bill-chaser" element={<BillChaserPage />} />
        
        {/* Phone System routes */}
        <Route path="/phone-system" element={<PhoneSystemDashboard />} />
        <Route path="/phone-system/browser-call" element={<BrowserCall />} />
        <Route path="/phone-system/numbers" element={<PhoneNumbersList />} />
        <Route path="/phone-system/recordings" element={<CallRecordings />} />
        <Route path="/phone-system/assignments" element={<PhoneNumberAssignmentAdmin />} />

        {/* Vapi Voice AI routes */}
        <Route path="/vapi" element={<VapiDashboard />} />
        <Route path="/vapi/web-call" element={<WebCall />} />
        <Route path="/vapi/call-logs" element={<CallLogs />} />
        <Route path="/vapi/call/:callId" element={<CallDetails />} />
        <Route path="/vapi/workflows" element={<Workflows />} />
        <Route path="/vapi/todos" element={<VapiTodos />} />

        {/* Product routes */}
        <Route path="/products/new" element={<NewProductForm />} />
        <Route path="/products/:id" element={<ProductView />} />
        <Route path="/products" element={<AllProducts />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/order/:id" element={<OrderDetails />} />

        {/* Project routes */}
        <Route path="/projects/new" element={<NewProject />} />
        <Route path="/project/:id" element={<ProjectPage />} />
        <Route path="/project/:id/timeline" element={<TimelineView />} />
        <Route path="/projects" element={<ProjectsList />} />

        {/* Email routes */}
        <Route path="/emails" element={<EmailsList />} /> {/* Client's personal email list - filtered to show only emails assigned to them */}
        <Route path="/emails/new" element={<NewEmail />} /> {/* Compose and send new emails */}
        <Route path="/emails/address-book" element={<AddressBook />} /> {/* MASTER ADMIN: All email contacts tracked by system */}
        <Route path="/emails/admin/accounts" element={<EmailAccountsAdmin />} /> {/* MASTER ADMIN: Assign email accounts to client users */}
        <Route path="/email/:id" element={<EmailView />} /> {/* View individual outbound email details */}
        <Route path="/inbox/:id" element={<InboundEmailDetail />} /> {/* View individual inbound email details */}

        {/* Session/Booking routes */}
        <Route path="/sessions/types" element={<SessionTypesPage />} />
        <Route path="/sessions/types/create" element={<CreateSessionType />} />
        <Route path="/sessions/edit/:id" element={<EditSessionType />} />
        <Route path="/sessions/set-availabilities" element={<SetAvailabilities />} />
        <Route path="/sessions/set-availabilities/:practitionerId" element={<SetAvailabilities />} />
        <Route path="/sessions/practitioner-calendar/:practitionerId" element={<ClientBookingCalendarByPractitionerId />} />
        <Route path="/schedule-session" element={<ScheduleSession />} />
        <Route path="/sessions/client-booking-calendar" element={<ClientBookingCalendar />} />
        <Route path="/bookings/:bookingId" element={<BookingDetails />} />
        
        {/* Knowledge Base routes - Admin only */}
        <Route path="/knowledgebase" element={<KnowledgeBaseList />} />
        <Route path="/knowledgebase/:slug" element={<ArticleViewer />} />

        {/* Subscription/Billing routes */}
        <Route path="/subscriptions" element={<SubscriptionsDashboard />} />                    {/* ADMIN: Overview dashboard of all client subscriptions, revenue stats */}
        <Route path="/subscriptions/manage" element={<ManageSubscriptions />} />                {/* ADMIN: Manage existing subscriptions (cancel, modify, view details) */}
        <Route path="/subscriptions/create" element={<CreateSubscription />} />                 {/* ADMIN: Create new recurring subscriptions for clients */}
        <Route path="/subscriptions/payment-methods" element={<PaymentMethods />} />            {/* ADMIN: Add/remove client credit cards for future billing */}
        <Route path="/subscriptions/invoices" element={<InvoiceHistory />} />                   {/* ADMIN/CLIENT: View invoice history and payment receipts */}
        <Route path="/subscriptions/settings" element={<BillingSettings />} />                  {/* ADMIN: Configure billing settings, tax rates, company info */}
        <Route path="/subscriptions/manual-charging" element={<ManualCharging />} />            {/* ADMIN: One-time charges using saved payment methods */}
        <Route path="/subscriptions/:id/edit" element={<EditSubscription />} />                 {/* ADMIN: Edit existing subscription details */}

        {/* Admin routes */}
        <Route path="/admin" element={<AdminDashboard />} />

        <Route path="/admin/deploy" element={<DeploymentManager />} />
        <Route path="/admin/tenant-deployment" element={<TenantDeploymentManager />} />
        <Route path="/admin/tenants" element={<TenantsList />} />
        <Route path="/admin/tenants/new" element={<CreateTenant />} />
        <Route path="/admin/tenants/:id/edit" element={<EditTenant />} />

        {/* Profile routes */}
        <Route path="/profile" element={<ViewProfile />} />                        {/* USER: View profile details */}
        <Route path="/profile/edit" element={<EditProfile />} />                  {/* USER: Edit profile information */}
        <Route path="/profile/settings" element={<Settings />} />                  {/* USER: Manage profile settings */}
        <Route path="/profile/shipping" element={<ShippingAddresses />} />         {/* USER: Manage shipping addresses */}
        <Route path="/profile/payment-methods" element={<ProfilePaymentMethods />} />     {/* USER: Manage payment methods */}
        <Route path="/profile/subscription-offers" element={<SubscriptionOffers />} />     {/* USER: View subscription offers */}
        <Route path="/profile/subscriptions" element={<CurrentSubscriptions />} />     {/* USER: Manage current subscriptions */}

        {/* Seller Profile routes */}
        <Route path="/seller-profiles" element={<SellerProfilesList />} />         {/* PUBLIC: View all seller profiles */}
        <Route path="/seller-profile/:id" element={<SellerProfileView />} />       {/* PUBLIC: View individual seller profile */}
        <Route path="/seller-profiles/new" element={<NewSellerProfile />} />       {/* USER: Create new seller profile */}

        {/* Research & Development routes */}
        <Route path="/researchanddesign" element={<ResearchAndDesignDashboard />} />  {/* USER: R&D dashboard and project management */}
        <Route path="/researchanddesign/projects" element={<ResearchAndDesignProjects />} />  {/* USER: R&D projects list */}
        <Route path="/researchanddesign/projects/new" element={<ResearchAndDesignProjectWizard />} />  {/* USER: Create new R&D project */}
        <Route path="/researchanddesign/projects/:id" element={<ResearchAndDesignProjectDetail />} />  {/* USER: R&D project details */}
        <Route path="/researchanddesign/projects/:id/edit" element={<ResearchAndDesignProjectEdit />} />  {/* USER: Edit R&D project */}
        <Route path="/researchanddesign/timesheet" element={<ResearchAndDesignTimesheet />} />  {/* USER: R&D time tracking */}
        <Route path="/researchanddesign/evidence" element={<ResearchAndDesignEvidence />} />  {/* USER: R&D evidence upload */}
        <Route path="/researchanddesign/evidence/upload" element={<ResearchAndDesignUploadEvidence />} />  {/* USER: Upload new evidence */}
        <Route path="/researchanddesign/evidence/:evidenceId" element={<ResearchAndDesignEvidenceDetails />} />  {/* USER: View/edit evidence details */}
        <Route path="/researchanddesign/record-audio" element={<ResearchAndDesignRecordAudio />} />  {/* USER: R&D audio recording */}
        <Route path="/researchanddesign/activities/new" element={<ResearchAndDesignNewActivity />} />  {/* USER: Create new R&D activity */}
        <Route path="/researchanddesign/activities/:id" element={<ResearchAndDesignActivityDetails />} />  {/* USER: View R&D activity details */}
        <Route path="/researchanddesign/activities/:id/edit" element={<ResearchAndDesignEditActivity />} />  {/* USER: Edit R&D activity */}
        <Route path="/researchanddesign/gaps" element={<ResearchAndDesignGapAnalysis />} />  {/* USER: R&D gap analysis */}
        <Route path="/researchanddesign/reports" element={<ResearchAndDesignReports />} />  {/* USER: R&D reports and analytics */}
        <Route path="/researchanddesign/transcribe" element={<ResearchAndDesignTranscribe />} />  {/* USER: Audio transcription tool */}

        {/* Training Center routes */}
        <Route path="/hamish" element={<HamishTraining />} />  {/* USER: Training videos and resources */}

        {/* Domains module routes */}
        <Route path="/domains" element={<DomainsPage />} />  {/* USER: Domain search and management */}
        <Route path="/domains/:id" element={<DomainDetails />} />  {/* USER: Domain details and DNS management */}

        {/* Offers routes */}
        <Route path="/offers/maintenance" element={<MaintenanceOffer />} />  {/* PUBLIC: Business maintenance subscription offer */}

        {/* Test routes */}
        <Route path="/test/sendemail" element={<SendTestEmail />} />
        <Route path="/test/upload" element={<TestUploadFile />} />
        <Route path="/test/upload-encrypted" element={<TestUploadEncryptedFile />} />
        <Route path="/test/auth" element={<TestAuth />} />
        <Route path="/test/cart" element={<TestClientCart />} />
      </Routes>
    </>
  );
};

export const App = () => {
  // Check and set tenantId if missing
  React.useEffect(() => {
    const storedTenantId = localStorage.getItem('tenantId');
    if (!storedTenantId) {
      // Use the tenantId from brandConfig as default
      if (brandConfig.tenantId) {
        localStorage.setItem('tenantId', brandConfig.tenantId);
      }
    }
  }, []);

  return (
    <Router>
      <ApolloProvider client={apolloClient}>
        <AuthProvider>
          <ChakraProvider theme={theme}>
            <AppContent />
          </ChakraProvider>
        </AuthProvider>
      </ApolloProvider>
    </Router>
  );
};
